import {autoinject} from "aurelia-framework";
import {HttpClient, HttpResponseMessage} from "aurelia-http-client";
import {EventAggregator} from 'aurelia-event-aggregator';
import {UpdateShoppingCartMessage} from "./events/update-shopping-cart-message";
import {Router} from "aurelia-router";
import config from "./config";


@autoinject
export class CartService {

    private cart: Cart = new Cart();

    private shippingAddress: Address = new Address();

    private billingAddress: Address = new Address();

    public shippingCost: [string, number] = ["0", 0];

    private paymentMethod: PaymentMethod = PaymentMethod.CreditCard;

    private orderId: string = "";


    constructor(private eventAggregator: EventAggregator,
                private http: HttpClient,
                private router: Router) {

        http.configure(x => {
        x.withBaseUrl(config.baseUrl);
        x.withHeader('Content-Type', 'application/json');
        });

        eventAggregator.subscribe(UpdateShoppingCartMessage, message => {
            this.saveToSessionStorage()
        });
        let cartJSON = window.sessionStorage.getItem("cart");
        if (cartJSON) {
            try {
                this.cart = JSON.parse(cartJSON)
            } catch (ignore) {
            }
        }

        let billingJSON = window.sessionStorage.getItem("billingAddress");
        if (billingJSON) {
            try {
                this.billingAddress = JSON.parse(billingJSON)
            } catch (ignore) {
            }
        }

        let shippingJSON = window.sessionStorage.getItem("shippingAddress");
        if (shippingJSON) {
            try {
                this.shippingAddress = JSON.parse(shippingJSON)
            } catch (ignore) {
            }
        }
    }

    private saveToSessionStorage() {
        window.sessionStorage.setItem("cart", JSON.stringify(this.cart));
    }

    public getCart(): Promise<Cart> {
        return new Promise<Cart>(((resolve, reject) => {
            resolve(this.cart)
        }));
    }

    public setPaymentMethod(paymentMethodName: string) {
        if (paymentMethodName === PaymentMethod.PayPal.toString()) {
          this.paymentMethod = PaymentMethod.PayPal
        } else if (paymentMethodName === PaymentMethod.CreditCard.toString()) {
            this.paymentMethod = PaymentMethod.CreditCard
        } else {
            this.paymentMethod = PaymentMethod.AdvancedPayment
        }
    }

    public getShippingAddress(): Promise<Address> {
        return new Promise<Address>((resolve, reject) => {
            resolve(this.shippingAddress)
        })
    }

    public getBillingAddress(): Promise<Address> {
        return new Promise<Address>((resolve, reject) => {
            resolve(this.billingAddress)
        })
    }

    public saveShippingAddress(address: Address) {
        this.shippingAddress = address;
        window.sessionStorage.setItem("shippingAddress", JSON.stringify(this.shippingAddress))
    }

    public saveBillingAddress(address: Address) {
        this.billingAddress = address;
        window.sessionStorage.setItem("billingAddress", JSON.stringify(this.billingAddress))
    }

    public addGiftCard(giftCard: GiftCard) {
        this.cart.giftCards.push(giftCard);

        this.eventAggregator.publish(new UpdateShoppingCartMessage())
    }

    public getShippingCostString(): string {
        return this.shippingCost[0]
    }

    public getShippingCostAmount(): number {
        return this.shippingCost[1]
    }

    public getTotalAmount(): number {
        return this.getSum() + this.shippingCost[1]
    }

    public getSum(): number {
        let sum = 0;
        this.cart.giftCards.forEach(giftCard => {
            sum += giftCard.count * giftCard.amount
        });
        return sum;
    }

    public getOrderId() : string {
        return this.orderId ? this.orderId : ""
    }

    public async purchase():Promise<any> {
        let content = new Content(
            `${this.shippingAddress.first_name} ${this.shippingAddress.last_name}`,
            this.shippingAddress,
            this.billingAddress ? this.billingAddress : this.shippingAddress,
            this.cart.giftCards,
            this.paymentMethod
        );

        const response = await  this.http.createRequest(config.paymentUrl)
          .asPost()
          .withContent(JSON.stringify(content))
          .send();

        if (response.isSuccess) {
            this.extractOderId(response);
            if (this.paymentMethod === PaymentMethod.CreditCard || this.paymentMethod === PaymentMethod.PayPal) {
                this.sendToConCardis(response);
            } else {
                this.showPaymentInformationPage();
            }
        }
    }

    public paymentSucceeded() {
        this.cart.giftCards = [];
        this.saveToSessionStorage();
        this.eventAggregator.publish(new UpdateShoppingCartMessage())
    }

    private sendToConCardis(response: HttpResponseMessage) {

        let content = response.content;

        if (content.hasOwnProperty("order_id") && content.hasOwnProperty("sha_in") && content.hasOwnProperty("total_amount")) {
            let form = document.createElement("form");

            form.setAttribute("method", "post");
            form.setAttribute("action", config.concardisUrl);

            let params = {};
            params['PSPID'] = "40F04242";
            params['ORDERID'] = content.order_id;
            params['AMOUNT'] = content.total_amount;
            params['CURRENCY'] = "EUR";
            params['LANGUAGE'] = "de_DE";
            params['CN'] = `${this.shippingAddress.first_name} ${this.shippingAddress.last_name}`;
            params['EMAIL'] = this.shippingAddress.e_mail;
            params['OWNERZIP'] = this.shippingAddress.zip;
            params['OWNERADDRESS'] = this.shippingAddress.street;
            params['OWNERCTY'] = this.shippingAddress.country;
            params['OWNERTOWN'] = this.shippingAddress.city;
            params['TITLE'] = "New Yorker";
            params['SHASIGN'] = content.sha_in;
            params['TP'] = "payment_template.html";

            for (let key in params) {
                if (params.hasOwnProperty(key)) {
                    let hiddenField = document.createElement("input");
                    hiddenField.setAttribute("type", "hidden");
                    hiddenField.setAttribute("name", key);
                    hiddenField.setAttribute("value", params[key]);

                    form.appendChild(hiddenField);
                }
            }

            document.body.appendChild(form);
            form.submit();
        }
    }

    private showPaymentInformationPage(): any {
        this.router.navigate("page6-paymentInformation")
    }

    private extractOderId(response: any) {
        let content = response.content;

        if (content.hasOwnProperty("order_id")) {
            this.orderId = content["order_id"]
        }
    }
}

export class Cart {
    public giftCards: GiftCard[] = [];
}

export class GiftCard {
    constructor() {
        this.count = 1
    }

    private order_type: String = "GC";
    public amount: number;
    public count: number;
}

export class Address {
    first_name: string;
    last_name: string;
    street: string;
    e_mail: string;
    city: string;
    zip: string;
    country: string = "DE"
}

export class Legals {
    privacyConfirmed: boolean = true;
    termsOfServiceConfirmed: boolean = false;
    returnPolicyConfirmed: boolean = false;
    legalAge: boolean = false;
}

export enum PaymentMethod {
    PayPal = "PAYPAL", CreditCard = "CreditCard", AdvancedPayment = "AdvancedPayment"
}

export class Content {
    constructor(private customer: string,
                private shipping_address: Address,
                private invoice_address: Address,
                private items: GiftCard[],
                private payment_method: PaymentMethod) {
    }
}